import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import filter from "lodash/filter"
import assign from "lodash/assign"
import { useState, useEffect } from "react"
import dayjs from "utils-lib/date"
import { ReactSVG } from "react-svg"
import { makeStyles } from "tss-react/mui"
import Box from "component-lib/Base/components/Box"
import getContentUrl from "utils-lib/getContentUrl"
import { getWindow } from "utils-lib/getWindow"
import Typography from "component-lib/Base/components/Typography"
import { Link, FormattedTextField, Button } from "../../../../../Base"

const _map = map.bind(map)

const ssrWindow = getWindow()

const alertIconSvg = `/content/dam/wm/icons/red-alert-exclamation-circle.svg`

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: `1rem .5rem 1rem 1.5rem`,
    marginRight: `1rem`,
    width: `100%`,
    display: `grid`,
    flexDirection: `column`,
    [theme.breakpoints.down(`md`)]: {
      alignItems: `flex-start`,
      width: `100%`,
      maxWidth: `unset`,
      margin: `0.5rem`,
      padding: `2rem`,
      paddingLeft: `0.5rem`,
      borderRadius: `.5rem`,
      backgroundColor: get(theme, `palette.common.white`, ``),
      boxShadow: `0 2px 10px 0 rgba(0,0,0,0.2)`,
      "&>div": {
        width: `15.5em`,
      },
    },
    "&:hover": {
      cursor: `pointer`,
      backgroundColor: `${get(
        theme,
        `palette.background.gray`,
        ``,
      )} !important`,
    },
  },
  itemInnerWrapper: {
    width: `auto`,
  },
  leftOffset: {
    paddingLeft: `1.75rem`,
  },
  alertIcon: {
    display: `flex`,
    alignItems: `center`,
    width: `1.25rem`,
    "& svg": {
      height: `1.25rem`,
    },
    marginRight: `.25rem`,
  },
  material: {
    // [theme.breakpoints.down(`md`)]: {
    //   whiteSpace: `nowrap`,
    // }
  },
  divider: {
    right: `0.5rem`,
    [theme.breakpoints.down(`md`)]: {
      display: `none`,
    },
  },
  timeSlotWrapper: {
    display: `flex`,
    marginLeft: `.25rem`,
    flexWrap: `wrap`,
    "& .segment": {
      padding: `0 1.5rem`,
      "& .slot": {
        width: `5.5rem`,
      },
      "& .begin": {
        paddingRight: `0.5rem`,
      },
      "& .end": {
        paddingLeft: `0.5rem`,
      },
    },
  },
}))

export const FacilityListItem = ({
  strings,
  map,
  item,
  onSelectFacility,
  onMouseEnterItem = () => {},
  onMouseLeaveItem = () => {},
  siteLanguage,
  facilityAlerts,
}) => {
  const { classes } = useStyles()
  const [fProps, setFProps] = useState(item.properties)

  useEffect(() => {
    getFProps(item.properties)
  }, [item])

  const replaceAddressSpaces = (s) => s.replace(/ /g, `+`)

  const createAddress = (street, city, state, zip) =>
    `${replaceAddressSpaces(street)}+${replaceAddressSpaces(
      city,
    )}+${replaceAddressSpaces(state)}+${replaceAddressSpaces(zip)}`

  // Redirect to get google map directions.
  const redirectGoogle = (fromAddress, toAddress) => {
    const url = `https://www.google.com/maps/dir/${fromAddress}/${toAddress}`
    ssrWindow.open(url, `_blank`)
  }

  function getFProps(properties) {
    const lang = get(siteLanguage, `language`, ``)

    const csrOpHours = get(properties, `csrOpHours`, ``)
    const materialsAccepted = get(properties, `materialsAccepted`, ``)

    setFProps(
      assign({}, properties, {
        csrOpHours,
        materialsAccepted,
        // target the space between holiday and regular hours with the following regex
        regex: lang === `fr_CA` ? /Ferm.+:/ : /Closed/,
      }),
    )
  }

  function renderAddress() {
    const address = get(fProps, `address`, ``)
    const city = get(fProps, `city`, ``)
    const state = get(fProps, `state`, ``)
    const zip = get(fProps, `zip`, ``)

    return (
      <Box display="flex" flexDirection="column" className={classes.leftOffset}>
        <Typography>{address}</Typography>
        <Box display="flex" flexWrap="wrap">
          <Typography
            style={{ marginRight: `0.5rem` }}
          >{`${city}, ${state} ${zip}`}</Typography>
          <Box className={classes.divider}>|</Box>
          <Link
            shouldNotTrackInComponent
            className="maax-font-weight-regular pl-1"
            onClick={(e) => {
              e.stopPropagation()

              const customerAddress = map.selectedGeosuggestion.label // map.customerAddress
              const fromAddress = replaceAddressSpaces(customerAddress)
              const toAddress = createAddress(address, city, state, zip)

              redirectGoogle(fromAddress, toAddress)
            }}
          >
            {strings.GET_DIRECTIONS}
          </Link>
        </Box>
      </Box>
    )
  }

  function renderHours(facilityAlert) {
    const today = dayjs().format(`dddd`)
    const todaysHours = get(fProps, `csrOpHours.${today}`, ``)

    const hasFacilityAlert = !isEmpty(facilityAlert)
    let alertReason = get(facilityAlert, `reasonForHourChangeClosure`, ``)
    if (alertReason === `Other`) {
      alertReason = get(facilityAlert, `other`, ``)
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        mt="1rem"
        className={hasFacilityAlert ? "" : classes.leftOffset}
      >
        {hasFacilityAlert && alertReason ? (
          <Box display="flex">
            <ReactSVG
              wrapper="span"
              className={classes.alertIcon}
              src={getContentUrl(alertIconSvg)}
            />
            <Typography style={{ marginLeft: `.25rem` }}>
              {alertReason}
            </Typography>
          </Box>
        ) : (
          todaysHours && (
            <Box display="flex">
              <Typography variant="subheading2">Open</Typography>
              <Box
                className={classes.timeSlotWrapper}
                display="flex"
                ml=".25rem"
              >
                {todaysHours &&
                  _map(todaysHours, (timeSlot, index) => (
                    <Box display="flex" className="segment" key={index}>
                      <Typography variant="body1" className="slot begin">
                        {get(timeSlot, `open`, ``)}
                      </Typography>
                      <Typography variant="body1">-</Typography>
                      <Typography variant="body1" className="slot end">
                        {get(timeSlot, `close`, ``)}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          )
        )}
      </Box>
    )
  }

  const { name, uniqueId } = fProps
  const materialsAccepted = get(fProps, `materialsAccepted`, ``)

  // const facilityAlert = _.filter(
  //   facilityAlerts,
  //   (alert) => _.get(alert, `facilities.id`, ``) === `S09875`,
  // )
  const facilityAlert = filter(
    facilityAlerts,
    (alert) => get(alert, `facilities.id`, ``) === uniqueId,
  )

  // const iconFac = facTypeCode === `3` ? iconRecycleSvg : iconTrashSvg
  return (
    <div
      className={`FacilityListItem ${classes.root}`}
      onMouseEnter={() => onMouseEnterItem(uniqueId)}
      onMouseLeave={() => onMouseLeaveItem()}
      data-testid="FacilityListItem"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        className={classes.leftOffset}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation()
            onSelectFacility(uniqueId)
          }}
          overrideClass="p-0 wm-link"
        >
          {name}
        </Button>
        {fProps.distance && (
          <Typography variant="overline" style={{ whiteSpace: `nowrap` }}>
            {parseFloat(get(fProps, `distance`, 0)).toFixed(2)} mi
          </Typography>
        )}
      </Box>
      <Box className={classes.itemInnerWrapper}>
        {renderAddress()}
        {renderHours(
          get(facilityAlert, `[${get(facilityAlert, `length`, ``) - 1}]`, ``),
        )}
        {!isEmpty(materialsAccepted) && (
          <Box display="flex" className={classes.leftOffset} mt="1rem">
            <Typography variant="subheading2">
              {strings.ACCEPTED_MATERIALS}
            </Typography>
            <Box className={classes.timeSlotWrapper}>
              {_map(materialsAccepted, (mat, key) => (
                <FormattedTextField
                  key={key}
                  variant="body1"
                  className={`${classes.leftOffset} ${classes.material}`}
                >
                  {key === `nonHazardous`
                    ? strings.NONHAZARDOUS
                    : strings.HAZARDOUS}
                </FormattedTextField>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </div>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// FacilityListItem.propTypes = {
//   strings: PropTypes.object.isRequired,
//   map: PropTypes.object.isRequired,
//   item: PropTypes.object.isRequired,
//   onSelectFacility: PropTypes.func.isRequired,
//   onMouseEnterItem: PropTypes.func,
//   onMouseLeaveItem: PropTypes.func,
//   siteLanguage: PropTypes.object.isRequired,
//   facilityAlerts: PropTypes.array.isRequired,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// FacilityListItem.defaultProps = {
//   onMouseEnterItem: () => {},
//   onMouseLeaveItem: () => {},
// }
